/**
 * Browser and platform detection utility
 * Detects user's browser, OS, and device type for providing specific instructions
 */

export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  // Detect browser
  let browser = 'unknown';
  if (userAgent.includes('firefox')) {
    browser = 'firefox';
  } else if (userAgent.includes('edg')) {
    browser = 'edge';
  } else if (userAgent.includes('chrome')) {
    browser = 'chrome';
  } else if (userAgent.includes('safari')) {
    browser = 'safari';
  } else if (userAgent.includes('opera') || userAgent.includes('opr')) {
    browser = 'opera';
  }

  // Detect OS
  let os = 'unknown';
  if (userAgent.includes('android')) {
    os = 'android';
  } else if (
    userAgent.includes('iphone') ||
    userAgent.includes('ipad') ||
    userAgent.includes('ipod')
  ) {
    os = 'ios';
  } else if (userAgent.includes('win')) {
    os = 'windows';
  } else if (userAgent.includes('mac')) {
    os = 'macos';
  } else if (userAgent.includes('linux')) {
    os = 'linux';
  }

  // Detect device type
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    );
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(
    userAgent
  );

  return {
    browser,
    os,
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    userAgent,
    platform,
  };
};

/**
 * Get browser-specific instructions for enabling location services
 */
/**
 * Detect if the page is being viewed in an in-app web view
 * Checks for custom user agent strings set by Flutter apps
 * @returns {Object} { isInAppWebView: boolean, appName: string | null }
 */
export const detectInAppWebView = () => {
  const userAgent = navigator.userAgent;

  // Check for custom app user agents
  const appPatterns = [{ pattern: /MadnessTime/i, name: 'MadnessTime' }];

  for (const { pattern, name } of appPatterns) {
    if (pattern.test(userAgent)) {
      return {
        isInAppWebView: true,
        appName: name,
      };
    }
  }

  return {
    isInAppWebView: false,
    appName: null,
  };
};

export const getLocationInstructions = () => {
  const detection = detectBrowser();
  const { browser, os, isMobile } = detection;

  const instructions = {
    chrome: {
      desktop: {
        title: 'Chrome (Desktop)',
        steps: [
          'Click the lock icon 🔒 in the address bar',
          'Find "Location" in the permissions list',
          'Change it from "Block" to "Allow"',
          'Refresh the page',
        ],
        url: 'https://support.google.com/chrome/answer/142065',
      },
      mobile: {
        title: 'Chrome (Mobile)',
        steps: [
          'Tap the three dots menu (⋮) in the top right',
          'Go to Settings → Site Settings → Location',
          'Make sure Location is enabled',
          'Find this site and set to "Allow"',
        ],
        url: 'https://support.google.com/chrome/answer/142065',
      },
    },
    firefox: {
      desktop: {
        title: 'Firefox (Desktop)',
        steps: [
          'Click the lock icon 🔒 in the address bar',
          'Click "Connection secure" → More Information',
          'Go to the Permissions tab',
          'Find "Access Your Location" and uncheck "Use Default"',
          'Select "Allow"',
        ],
        url: 'https://support.mozilla.org/en-US/kb/permissions-manager-give-ability-store-passwords-set-cookies-more',
      },
      mobile: {
        title: 'Firefox (Mobile)',
        steps: [
          'Tap the three dots menu (⋮)',
          'Go to Settings → Site Permissions → Location',
          'Find this site and set to "Allow"',
        ],
        url: 'https://support.mozilla.org/en-US/kb/how-firefox-android-use-permissions-it-requests',
      },
    },
    safari: {
      desktop: {
        title: 'Safari (macOS)',
        steps: [
          'Click Safari in the menu bar → Settings',
          'Go to the Websites tab',
          'Select Location in the left sidebar',
          'Find this website and change to "Allow"',
        ],
        url: 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac',
      },
      mobile: {
        title: 'Safari (iOS)',
        steps: [
          'Open iPhone Settings app',
          'Scroll down and tap Safari',
          'Tap Location',
          'Select "Ask" or "Allow"',
          'Return to Safari and refresh the page',
        ],
        url: 'https://support.apple.com/en-us/HT203033',
      },
    },
    edge: {
      desktop: {
        title: 'Microsoft Edge',
        steps: [
          'Click the lock icon 🔒 in the address bar',
          'Click "Permissions for this site"',
          'Find Location and change to "Allow"',
          'Refresh the page',
        ],
        url: 'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04',
      },
      mobile: {
        title: 'Microsoft Edge (Mobile)',
        steps: [
          'Tap the three dots menu (⋮)',
          'Go to Settings → Site permissions → Location',
          'Make sure Location is enabled',
          'Find this site and set to "Allow"',
        ],
        url: 'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04',
      },
    },
    opera: {
      desktop: {
        title: 'Opera',
        steps: [
          'Click the lock icon 🔒 in the address bar',
          'Click "Site settings"',
          'Find Location and change to "Allow"',
          'Refresh the page',
        ],
        url: 'https://help.opera.com/en/latest/web-preferences/#sitePreferences',
      },
      mobile: {
        title: 'Opera (Mobile)',
        steps: [
          'Tap the Opera menu',
          'Go to Settings → Site settings → Location',
          'Find this site and set to "Allow"',
        ],
        url: 'https://help.opera.com/en/latest/web-preferences/',
      },
    },
  };

  // Get OS-specific system settings instructions
  const systemInstructions = {
    ios: {
      title: 'iOS System Settings',
      steps: [
        'Open Settings app on your iPhone/iPad',
        'Tap Privacy & Security',
        'Tap Location Services',
        'Make sure Location Services is ON',
        'Scroll down and find Safari (or your browser)',
        'Set to "While Using the App" or "Always"',
      ],
      url: 'https://support.apple.com/en-us/HT207092',
    },
    android: {
      title: 'Android System Settings',
      steps: [
        'Open Settings app',
        'Tap Location (or Security & Location)',
        'Make sure Location is ON',
        'Tap App permissions → Location',
        'Find your browser and set to "Allow"',
      ],
      url: 'https://support.google.com/accounts/answer/3467281',
    },
    windows: {
      title: 'Windows System Settings',
      steps: [
        'Open Settings',
        'Go to Privacy & Security → Location',
        'Make sure Location services is ON',
        'Scroll down and make sure your browser can access location',
      ],
      url: 'https://support.microsoft.com/en-us/windows/windows-location-service-and-privacy-3a8eee0a-5b0b-dc07-eede-2a5ca1c49088',
    },
    macos: {
      title: 'macOS System Settings',
      steps: [
        'Open System Settings (or System Preferences)',
        'Click Privacy & Security',
        'Click Location Services',
        'Make sure Location Services is enabled',
        'Find your browser in the list and check the box',
      ],
      url: 'https://support.apple.com/guide/mac-help/change-location-services-settings-mh35873/mac',
    },
  };

  // Determine which instructions to show
  const browserKey = browser in instructions ? browser : 'chrome'; // Default to Chrome
  const deviceType = isMobile ? 'mobile' : 'desktop';
  const browserInstructions = instructions[browserKey][deviceType];
  const systemInstructions_specific =
    os in systemInstructions ? systemInstructions[os] : null;

  return {
    browser: browserInstructions,
    system: systemInstructions_specific,
    detection,
    fallbackUrl: `https://www.google.com/search?q=how+to+enable+location+services+${browser}+${os}`,
  };
};
